import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  OnDestroy,
  OnInit,
  ViewEncapsulation
} from '@angular/core';
import { filter, Subscription } from 'rxjs';
import { GungToast, GungToastState, Notification } from '../../models/gung-notification/gung-notification';
import { GungNotificationService } from '../../services/notification/gung-notification.service';

const DEFAULT_DURATION = 6000;
const EXIT_DURATION = 600;

/**
 * The service replays its last notification to every new subscriber, and the toaster is recreated
 * whenever the app shell re-renders. Remembering what was shown keeps a replay from showing it twice,
 * while a notification sent before any toaster existed is still shown once.
 */
const shownNotifications = new WeakSet<Notification>();

/**
 * The app's notifications: shows everything sent through GungNotificationService.notify() as toasts.
 *
 * To show a notification, inject GungNotificationService and call notify(); never render this
 * component yourself. `<lib-gung-notification>` is already in every app wrapper (gung-standard,
 * BC, Fortnox, cloud-pim-base), and a second one shows every toast twice. The look follows the
 * shop's Bootstrap theme; restyle a shop with the --gung-toast-* CSS variables, not by overriding
 * the gung-toast classes. GungNotificationComponent (`lib-gung-notification-legacy`) is deprecated.
 *
 * Hovering a toast pauses every timer; hover is tracked per toast so one that leaves
 * while hovered cannot keep the rest paused.
 */
@Component({
  selector: 'lib-gung-notification',
  templateUrl: './gung-toaster.component.html',
  styleUrls: ['./gung-toaster.component.css'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class GungToasterComponent implements OnInit, OnDestroy {
  private notificationService = inject(GungNotificationService);
  private changeDetector = inject(ChangeDetectorRef);
  private subscription: Subscription;
  private timers = new Map<number, ReturnType<typeof setTimeout>>();
  private exitTimers = new Set<ReturnType<typeof setTimeout>>();
  private hoveredIds = new Set<number>();
  private nextId = 0;

  toasts: GungToast[] = [];
  bottomRight: GungToast[] = [];
  bottomCenter: GungToast[] = [];
  activeIds: number[] = [];

  ngOnInit(): void {
    this.subscription = this.notificationService
      .getNotificationSubject()
      .pipe(filter(notification => !!notification && !shownNotifications.has(notification)))
      .subscribe(notification => {
        shownNotifications.add(notification);
        this.add(notification);
      });
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
    this.timers.forEach(timer => clearTimeout(timer));
    this.exitTimers.forEach(timer => clearTimeout(timer));
  }

  onHover(id: number, hovered: boolean): void {
    if (hovered) {
      this.hoveredIds.add(id);
      this.pauseTimers();
    } else {
      this.hoveredIds.delete(id);
      this.resumeTimers();
    }
    this.update();
  }

  dismiss(id: number): void {
    const toast = this.toasts.find(t => t.id === id);
    if (!toast || toast.exiting) {
      return;
    }
    clearTimeout(this.timers.get(id));
    this.timers.delete(id);
    this.hoveredIds.delete(id);
    this.resumeTimers();
    this.toasts = this.toasts.map(t => (t.id === id ? { ...t, exiting: true } : t));
    this.update();
    const exitTimer = setTimeout(() => {
      this.exitTimers.delete(exitTimer);
      this.toasts = this.toasts.filter(t => t.id !== id);
      this.update();
    }, EXIT_DURATION);
    this.exitTimers.add(exitTimer);
  }

  private add(notification: Notification): void {
    const toast: GungToast = {
      id: ++this.nextId,
      title: notification.title || notification.message,
      message: notification.title ? notification.message : '',
      state: this.toState(notification.type),
      link: notification.link,
      duration: notification.notificationTime || DEFAULT_DURATION,
      position: notification.customClass?.split(' ').includes('center-notification') ? 'bottom-center' : 'bottom-right',
      customClass: notification.customClass
    };
    this.toasts = [...this.toasts, toast];
    this.startTimer(toast);
    this.update();
  }

  private toState(type: string): GungToastState {
    if (type === 'danger') {
      return 'error';
    }
    return type === 'success' || type === 'warning' || type === 'error' ? type : 'info';
  }

  private startTimer(toast: GungToast): void {
    if (this.hoveredIds.size === 0) {
      this.timers.set(
        toast.id,
        setTimeout(() => this.dismiss(toast.id), toast.duration)
      );
    }
  }

  private pauseTimers(): void {
    this.timers.forEach(timer => clearTimeout(timer));
    this.timers.clear();
  }

  private resumeTimers(): void {
    if (this.hoveredIds.size === 0) {
      this.toasts.filter(toast => !toast.exiting && !this.timers.has(toast.id)).forEach(toast => this.startTimer(toast));
    }
  }

  /** One toast per stack is open: the hovered one, otherwise the newest */
  private update(): void {
    this.bottomRight = this.toasts.filter(toast => toast.position === 'bottom-right');
    this.bottomCenter = this.toasts.filter(toast => toast.position === 'bottom-center');
    const hoveredId = this.hoveredIds.values().next().value;
    this.activeIds = [this.bottomRight, this.bottomCenter].map(stack => {
      const live = stack.filter(toast => !toast.exiting);
      return live.some(toast => toast.id === hoveredId) ? hoveredId : live[live.length - 1]?.id;
    });
    this.changeDetector.markForCheck();
  }
}
